// Offline Database Engine using IndexedDB
// Stores tasks, reports, photos, equipment usage locally
// Auto-syncs when online

const DB_NAME = 'myanmarlink_field';
const DB_VERSION = 1;

let db = null;

export const initDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, DB_VERSION);
  req.onupgradeneeded = (e) => {
    const d = e.target.result;
    if (!d.objectStoreNames.contains('tasks')) d.createObjectStore('tasks', { keyPath: 'task_id' });
    if (!d.objectStoreNames.contains('sync_queue')) d.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
    if (!d.objectStoreNames.contains('photos')) d.createObjectStore('photos', { keyPath: 'id', autoIncrement: true });
    if (!d.objectStoreNames.contains('cache')) d.createObjectStore('cache', { keyPath: 'key' });
    if (!d.objectStoreNames.contains('conflicts')) d.createObjectStore('conflicts', { keyPath: 'id', autoIncrement: true });
  };
  req.onsuccess = (e) => { db = e.target.result; resolve(db); };
  req.onerror = (e) => reject(e);
});

const getStore = (name, mode = 'readonly') => {
  const tx = db.transaction(name, mode);
  return tx.objectStore(name);
};

// === TASKS (Local Cache) ===
export const saveTasksLocal = async (tasks) => {
  const store = getStore('tasks', 'readwrite');
  for (const t of tasks) store.put(t);
};

export const getLocalTasks = () => new Promise((resolve) => {
  const store = getStore('tasks');
  const req = store.getAll();
  req.onsuccess = () => resolve(req.result || []);
  req.onerror = () => resolve([]);
});

export const updateLocalTask = (task) => {
  const store = getStore('tasks', 'readwrite');
  store.put(task);
};

// === SYNC QUEUE ===
export const addToSyncQueue = (item) => {
  const store = getStore('sync_queue', 'readwrite');
  store.add({ ...item, queued_at: new Date().toISOString(), synced: false });
};

export const getSyncQueue = () => new Promise((resolve) => {
  const store = getStore('sync_queue');
  const req = store.getAll();
  req.onsuccess = () => resolve((req.result || []).filter(i => !i.synced));
  req.onerror = () => resolve([]);
});

export const markSynced = (id) => {
  const store = getStore('sync_queue', 'readwrite');
  const req = store.get(id);
  req.onsuccess = () => {
    if (req.result) { req.result.synced = true; req.result.synced_at = new Date().toISOString(); store.put(req.result); }
  };
};

export const clearSyncedItems = () => {
  const store = getStore('sync_queue', 'readwrite');
  const req = store.getAll();
  req.onsuccess = () => {
    for (const item of req.result || []) { if (item.synced) store.delete(item.id); }
  };
};

// === PHOTOS (Local Storage) ===
export const savePhotoLocal = (taskId, dataUrl) => {
  const store = getStore('photos', 'readwrite');
  store.add({ task_id: taskId, data: dataUrl, saved_at: new Date().toISOString(), uploaded: false });
};

export const getLocalPhotos = (taskId) => new Promise((resolve) => {
  const store = getStore('photos');
  const req = store.getAll();
  req.onsuccess = () => resolve((req.result || []).filter(p => !taskId || p.task_id === taskId));
  req.onerror = () => resolve([]);
});

// === CACHE (stats, inventory, etc.) ===
export const setCache = (key, data) => {
  const store = getStore('cache', 'readwrite');
  store.put({ key, data, cached_at: new Date().toISOString() });
};

export const getCache = (key) => new Promise((resolve) => {
  const store = getStore('cache');
  const req = store.get(key);
  req.onsuccess = () => resolve(req.result?.data || null);
  req.onerror = () => resolve(null);
});

// === CONFLICTS ===
export const logConflict = (item) => {
  const store = getStore('conflicts', 'readwrite');
  store.add({ ...item, detected_at: new Date().toISOString() });
};

export const getConflicts = () => new Promise((resolve) => {
  const store = getStore('conflicts');
  const req = store.getAll();
  req.onsuccess = () => resolve(req.result || []);
  req.onerror = () => resolve([]);
});
